/**
 * Les collègues à qui CONFIER une tâche — bornés à ceux qui voient l'AO quand elle en nomme un.
 *
 * ## La règle (décision PO n° 66 du 2026-10-03, choix par défaut D213)
 *
 * Le serveur (`services/notifications/_destinataire_d_une_tache`) refuse en 422 le destinataire
 * d'une tâche LIÉE à un appel d'offres s'il ne voit pas l'entité de cet AO : la tâche nomme sa
 * référence (« Date limite des questions — AO-2026-014 ») et se range sur son entité. La modale
 * proposait pourtant tous les collègues de la session (`useTransferRecipients`) : depuis le
 * siège, un collègue d'une agence sœur, que le serveur refusait ensuite.
 *
 * Quand le lien retenu est un AO, la liste est croisée avec `useDesignablesSurLaConsultation`
 * — le MÊME prédicat que le refus — en gardant l'ordre « derniers utilisés ». Sans lien d'AO, ou
 * tant que la liste de l'AO n'est pas lue (en route, en échec, ou TRONQUÉE au plafond de la route :
 * `nonListes`), la liste des collègues reste entière — une liste incomplète ne prouve pas
 * l'absence, comme pour `useLienRefuseAuDestinataire` ; le serveur juge de toute façon, et la
 * modale n'efface pas le choix « Un collègue ».
 *
 * ## Un choix fait AVANT de relier l'AO (`retenu`)
 *
 * Le sélecteur de fiche liée est SOUS le choix du destinataire : on choisit un collègue, PUIS on
 * relie l'AO. Le collègue sorti de la liste disparaissait de l'écran mais partait quand même, et
 * le serveur refusait la tâche. `retenu(id)` rend le choix s'il est ENCORE proposé, `''` sinon :
 * c'est lui qui part, pas l'identifiant gardé en mémoire (qui revient si l'on retire le lien).
 * `aDesCollegues` dit s'il y a des collègues dans la session, AVANT ce filtre : la liste vide d'un
 * AO que personne d'autre ne voit laisse donc « Moi-même » choisissable.
 *
 * ## Un geste qui part AU CLIC (`enCours`)
 *
 * Le menu de transfert rapide (`QuickTransferItems`) transfère dès qu'on clique un nom : il ne
 * peut pas offrir la liste entière le temps que celle de l'AO arrive. `enCours` dit que la liste
 * d'un AO lié est en route ; ce menu n'offre alors aucun collègue, et la modale « Plus
 * d'options » (`TransferDialog`) garde « Transférer » inactif.
 */

import { useCallback, useMemo } from 'react'

import { useDesignablesSurLaConsultation } from '@/components/shared/useDesignablesSurLaConsultation'
import { useTransferRecipients } from '@/hooks/useTransferRecipients'
import type { LinkedEntityValue } from '@/lib/linkedEntity'

import { aoDuLien } from './aoDuLien'
import { listeBorneeALAo } from './listeBorneeALAo'

export function useDestinatairesDeLaTache(
    entityId: string | null,
    lien: LinkedEntityValue | null,
): ReturnType<typeof useTransferRecipients> & {
    aDesCollegues: boolean
    retenu: (id: string) => string
    enCours: boolean
} {
    const destinataires = useTransferRecipients(entityId)
    const tenderId = aoDuLien(lien)
    const { personnes, nonListes, isLoading, error } = useDesignablesSurLaConsultation({
        entityId: entityId ?? '',
        tenderId,
        enabled: tenderId !== null,
    })
    const tous = destinataires.ordered
    const ordered = useMemo(() => {
        if (!listeBorneeALAo(tenderId, { isLoading, error, nonListes })) return tous
        const permis = new Set(personnes.map((p) => p.id))
        return tous.filter((collegue) => permis.has(collegue.id))
    }, [tous, tenderId, isLoading, error, nonListes, personnes])
    const retenu = useCallback(
        (id: string) => (id && ordered.some((collegue) => collegue.id === id) ? id : ''),
        [ordered],
    )
    const enCours = tenderId !== null && isLoading
    return { ...destinataires, ordered, aDesCollegues: tous.length > 0, retenu, enCours }
}
